import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import RestartAltOutlined from "@mui/icons-material/RestartAltOutlined";
import ModelSelector from "./ModelSelector";
import KlingO3FormPanel from "./KlingO3FormPanel";
import GenericModelFormPanel from "./GenericModelFormPanel";
import KlingMotionControlFormPanel from "./KlingMotionControlFormPanel";
import {
  categoryModelNote,
  estimateCredits,
  modelsForCategory,
} from "../categories";
import {
  getClipLimitForModel,
  maxDurationForModel,
  snapClipDuration,
} from "../durationOptions";
import {
  getModelFamily,
  migrateMediaForModel,
  modelNativeAudioNote,
  validateFormForModel,
} from "../modelProfiles";
import { getDefaultMultiPromptForModel } from "../videoPromptTemplateUtils";
import { useVideoPromptTemplates } from "../VideoPromptTemplatesContext";
import { estimateRetailUsd, fetchPoyoPricing } from "../pricing/retailPricing";
import {
  estimateJobCreditsFromPricing,
  fetchVideoPricing,
} from "../api/fetchVideoPricing";
import {
  reconcileScenes,
  scenesStructureKey,
  sceneDurationsFromForm,
  normalizeFormForMultiSpeakerDialogue,
} from "../scenePlan";
import { needsAutoStitch } from "../klingSegmentPlan";

export function defaultFormBase(category) {
  const model = category?.defaultModelId || "";
  return {
    title: "",
    prompt: "",
    uploads: [],
    imageUrlUploads: [],
    referenceImageUploads: [],
    referenceVideoUploads: [],
    referenceAudioUploads: [],
    duration: 10,
    aspectRatio: "16:9",
    resolution: "1080p",
    generateAudio: true,
    sound: true,
    multiShots: false,
    multiPrompt: getDefaultMultiPromptForModel(model),
    scenes: null,
    kling30Tier: "pro",
    wanMode: "text",
    characterOrientation: "image",
    motionResolution: "720p",
    seed: "",
    model: category?.defaultModelId || "",
    avatarStyle: "realistic",
    shotPrompts: [""],
    backgroundType: "white",
    avatarTier: "standard",
    veoVariant: "fast",
    hailuoResolution: "768p",
    promptOptimizer: true,
    soraStyle: "",
    soraStoryboard: false,
    voiceSource: "ai",
    klingVoicePreset: "",
    klingVoiceLabel: "",
    musicSource: "upload",
    libraryMusicJobId: null,
    libraryMusicTrackIndex: null,
    libraryMusicTitle: "",
    grokMode: "normal",
  };
}

export default function VideoForm({
  category,
  form,
  onChange,
  onSubmit,
  submitting,
  lang,
  onReset,
  hitAxios,
  onGenerateScene,
  generatingScenes,
  sceneStatuses,
}) {
  const theme = useTheme();
  const { getDefaultPromptForModel } = useVideoPromptTemplates();
  const models = modelsForCategory(category);
  const family = getModelFamily(form.model);
  const [poyoPricing, setPoyoPricing] = React.useState(null);
  const [videoPricing, setVideoPricing] = React.useState(null);

  const willStitch =
    needsAutoStitch(form.model, form.duration, {
      hailuoResolution: form.hailuoResolution,
      resolution: form.resolution,
    }) ||
    (Array.isArray(form.scenes) && form.scenes.length > 1);
  const segmentCount = willStitch
    ? Math.max(1, sceneDurationsFromForm(form).length)
    : 1;
  const credits = estimateJobCreditsFromPricing(form, videoPricing, {
    willStitch,
    segmentCount,
    fallback: (durationSec) =>
      estimateCredits({
        duration: durationSec,
        resolution: form.hailuoResolution || form.resolution,
        generateAudio: form.generateAudio,
        model: form.model,
        avatarTier: form.avatarTier,
        soraStoryboard: form.soraStoryboard,
      }),
  });

  React.useEffect(() => {
    (async () => {
      const data = await fetchPoyoPricing();
      if (data) setPoyoPricing(data);
    })();
  }, []);

  React.useEffect(() => {
    if (!hitAxios) return;
    fetchVideoPricing(hitAxios).then(setVideoPricing);
  }, [hitAxios]);

  // Auto-open scene plan when duration exceeds the model clip limit.
  // Multi-speaker @-tags stay on one clip — snap duration instead of stitching.
  React.useEffect(() => {
    const normalized = normalizeFormForMultiSpeakerDialogue(form);
    const nextScenes = reconcileScenes(normalized);
    const nextForm = { ...normalized, scenes: nextScenes };
    const durChanged = Number(nextForm.duration) !== Number(form.duration);
    const sameNull = form.scenes == null && nextScenes == null;
    const sameStructure =
      scenesStructureKey(form.scenes) === scenesStructureKey(nextScenes) &&
      Boolean(form.scenes?.length) === Boolean(nextScenes?.length);
    if (!durChanged && (sameNull || sameStructure)) return;
    onChange(nextForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync on duration/model/prompt/resolution
  }, [form.duration, form.model, form.hailuoResolution, form.resolution, form.prompt]);

  const estimatedRetailUsd = estimateRetailUsd(poyoPricing, form);

  const handleModelChange = (newModel) => {
    const migrated = migrateMediaForModel(form, newModel);
    const nextFamily = getModelFamily(newModel);
    const maxDur = maxDurationForModel();
    const rawDur = Number(form.duration) || 5;
    const isGrok15 = String(newModel || "").includes("grok-imagine-video");
    const nextLimit = getClipLimitForModel(newModel, {
      hailuoResolution: form.hailuoResolution,
      resolution: isGrok15 && !["480p", "720p"].includes(String(form.resolution))
        ? "720p"
        : form.resolution,
    });
    let nextDur = Math.min(Math.max(rawDur, 3), maxDur);
    if (nextLimit.allowed?.length) {
      nextDur = snapClipDuration(nextDur, nextLimit);
    } else if (nextLimit.maxSec != null && nextDur <= nextLimit.maxSec) {
      nextDur = snapClipDuration(nextDur, nextLimit);
    }
    const patch = {
      ...form,
      ...migrated,
      model: newModel,
      prompt: getDefaultPromptForModel(newModel),
      multiPrompt: getDefaultMultiPromptForModel(newModel),
      multiShots: nextFamily === "kling-o3" ? form.multiShots : false,
      scenes: null,
      duration: nextDur,
      grokMode: form.grokMode || "normal",
      ...(isGrok15 && !["480p", "720p"].includes(String(form.resolution))
        ? { resolution: "720p" }
        : {}),
    };
    onChange({ ...patch, scenes: reconcileScenes(patch) });
  };

  const validation = validateFormForModel(form, category?.id, lang);
  const audioNote = modelNativeAudioNote(form.model, lang);
  const modelNote = [categoryModelNote(category, lang), audioNote].filter(Boolean).join(" ");
  const panelProps = {
    form,
    onChange,
    lang,
    categoryId: category?.id,
    modelId: form.model,
    onGenerateScene,
    generatingScenes,
    sceneStatuses,
  };

  return (
    <Card
      variant="outlined"
      data-tour="video-studio-form"
      sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              {category?.icon && (
                <category.icon sx={{ fontSize: 20, color: "primary.main" }} />
              )}
              <Typography variant="h6" fontWeight={800}>
                {lang?.[category?.labelKey] || category?.fallback}
              </Typography>
            </Stack>
            <Tooltip title={lang?.videoStudioReset || "Reset"}>
              <IconButton size="small" onClick={onReset} sx={{ color: "text.secondary" }}>
                <RestartAltOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <TextField
            fullWidth
            size="small"
            label={lang?.videoStudioJobTitle || "Title"}
            value={form.title || ""}
            onChange={(e) => onChange({ ...form, title: e.target.value })}
            placeholder={lang?.videoStudioJobTitlePlaceholder || "e.g. Summer launch — hero clip"}
            helperText={
              lang?.videoStudioJobTitleHint ||
              "A short label for this video in your library (separate from the generation prompt)."
            }
            inputProps={{ maxLength: 120 }}
          />

          {models.length > 1 ? (
            <ModelSelector
              value={form.model}
              onChange={handleModelChange}
              models={models}
              note={modelNote}
              lang={lang}
            />
          ) : modelNote ? (
            <Typography variant="caption" color="text.secondary" lineHeight={1.55}>
              {modelNote}
            </Typography>
          ) : null}

          {family === "kling-motion-control" ? (
            <KlingMotionControlFormPanel {...panelProps} />
          ) : family === "kling-o3" ? (
            <KlingO3FormPanel {...panelProps} />
          ) : (
            <GenericModelFormPanel {...panelProps} />
          )}

          <Divider />

          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {lang?.videoStudioEstimatedCredits || "Estimated"}:{" "}
              <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
                {credits} {lang?.videoStudioCredits || "credits"}
              </Box>
              {estimatedRetailUsd != null && (
                <>
                  {" · "}
                  <Box component="span" sx={{ color: "text.primary", fontWeight: 800 }}>
                    ${estimatedRetailUsd.toFixed(2)}
                  </Box>
                </>
              )}
            </Typography>
            {!validation.valid && validation.message ? (
              <Typography variant="caption" color="error.main" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                {validation.message}
              </Typography>
            ) : null}
            <Button
              type="button"
              variant="contained"
              startIcon={<AutoAwesomeOutlined />}
              onClick={onSubmit}
              disabled={submitting || !validation.valid}
              fullWidth
              data-tour="video-studio-generate"
              sx={{ fontWeight: 800 }}
            >
              {family === "kling-o3" || family === "kling-3"
                ? lang?.videoStudioRun || "Run"
                : lang?.videoStudioGenerateCta || "Generate"}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Form state with DB default prompt for the category’s default model. */
export function buildDefaultForm(category, getDefaultPromptForModel) {
  const base = defaultFormBase(category);
  return {
    ...base,
    prompt: getDefaultPromptForModel(base.model) || "",
  };
}
